/// <reference path="../types/littleg-my-tools-globals.d.ts" />
(() => {
  if (window.__littleGMyToolsBoot) { window.__littleGMyToolsBoot(document); return; }

  /**
   * My Tools reads existing external owners; it does not own credentials,
   * entitlement decisions, CV persistence or the Core account-link contract.
   * @typedef {HTMLElement & { __myToolsCleanup?: () => void }} MyToolsElement
   * @typedef {{ getState?: () => any }} ReadOnlyWebSession
   */
  const MAX_TOOLS = 100;
  const MAX_TEXT_LENGTH = 180;
  const instances = new Map();

  /*
   * Core adapter contract:
   * - Listen for bubbled `littleg:my-tools-request` events.
   * - Resolve data for the authenticated session on the server, never by trusting
   *   a customer ID supplied by this page.
   * - Dispatch `littleg:my-tools-data` on document with detail:
   *   { panelId, requestId, tools: [{ title, remainingUses, canContinue,
   *     continueUrl, recentResult: { title, completedAt, url } }] }.
   *   recentResult and its fields may be omitted when there is no result.
   * - Core remains authoritative for entitlement and every continuation request.
   */

  /** @param {unknown} value */
  const safeCount = (value) => {
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return null;
    const count = Number(value);
    return Number.isSafeInteger(count) && count >= 0 ? count : null;
  };

  /** @param {unknown} value @param {number} [maxLength] */
  const safeText = (value, maxLength = MAX_TEXT_LENGTH) => {
    if (typeof value !== 'string') return '';
    return value.trim().slice(0, maxLength);
  };

  /** @param {unknown} value */
  const safeInternalUrl = (value) => {
    if (typeof value !== 'string' || value.length > 2048 || value.startsWith('//')) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || url.origin !== window.location.origin || url.username || url.password) return null;
      return url.href;
    } catch {
      // Reject malformed/out-of-origin URLs; do not expose an unsafe link.
      return null;
    }
  };

  /** @param {unknown} value */
  const safeDateLabel = (value) => {
    if (typeof value !== 'string' || value.length > 64) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
  };

  let requestSequence = 0;
  const makeRequestId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    if (window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }
    // Request IDs correlate responses; they are not authentication secrets.
    requestSequence++;
    return `${Date.now().toString(36)}-${requestSequence.toString(36)}`;
  };

  /** @param {any} template Shopify card template DOM supplied by the section.
   *  @param {any} tool Untrusted Core presentation data, field-validated below.
   */
  const renderTool = (template, tool) => {
    const fragment = template.content.cloneNode(true);
    const card = fragment.querySelector('[data-my-tool-card]');
    const title = safeText(tool?.title, 120);
    const remainingUses = safeCount(tool?.remainingUses);
    if (!card || !title) return null;

    card.querySelector('[data-tool-title]').textContent = title;
    card.querySelector('[data-tool-uses]').textContent = remainingUses === null ? '—' : String(remainingUses);

    const recent = tool.recentResult;
    const recentPanel = card.querySelector('[data-tool-recent]');
    const resultTitle = safeText(recent?.title, MAX_TEXT_LENGTH);
    if (recent && resultTitle) {
      recentPanel.hidden = false;
      card.querySelector('[data-tool-result-title]').textContent = resultTitle;
      const resultDate = card.querySelector('[data-tool-result-date]');
      const dateLabel = safeDateLabel(recent.completedAt);
      if (dateLabel) {
        resultDate.dateTime = recent.completedAt;
        resultDate.textContent = dateLabel;
        resultDate.hidden = false;
      }
      const resultUrl = safeInternalUrl(recent.url);
      const resultLink = card.querySelector('[data-tool-result-link]');
      if (resultUrl) {
        resultLink.href = resultUrl;
        resultLink.hidden = false;
      }
    }

    const continueUrl = safeInternalUrl(tool.continueUrl);
    const canContinue = tool.canContinue === true && Boolean(continueUrl);
    const continueLink = card.querySelector('[data-tool-continue]');
    if (canContinue) {
      continueLink.href = continueUrl;
      continueLink.removeAttribute('aria-disabled');
      continueLink.removeAttribute('tabindex');
    } else {
      continueLink.removeAttribute('href');
      continueLink.setAttribute('aria-disabled', 'true');
      continueLink.setAttribute('tabindex', '-1');
    }

    return fragment;
  };

  /** @param {Event} event */
  const handleToolsData = (event) => {
    const payload = /** @type {CustomEvent<any>} */ (event).detail;
    if (!payload || typeof payload !== 'object') return;
    const instance = instances.get(payload.panelId);
    if (!instance || instance.sessionStatus !== 'authenticated' || !instance.subject || !instance.requestId || !activeToolsTab() || payload.requestId !== instance.requestId) return;
    const liveState = window.LittleGWebSession?.getState?.();
    if (liveState?.status !== 'authenticated' || liveState.profile?.id !== instance.subject) return;

    const { root } = instance;
    if (instance.timer) window.clearTimeout(instance.timer);
    instance.timer = null;

    const list = root.querySelector('[data-my-tools-list]');
    const empty = root.querySelector('[data-my-tools-empty]');
    const status = root.querySelector('[data-my-tools-status]');
    const template = root.querySelector('[data-my-tools-card-template]');
    if (!Array.isArray(payload.tools) || !template) {
      root.dataset.state = payload.errorCode === 'shopify_account_not_linked'
        ? 'unlinked' : 'unavailable';
      list.replaceChildren();
      list.hidden = true;
      empty.hidden = true;
      status.textContent = root.dataset.state === 'unlinked'
        ? (root.dataset.accountLinkRequiredMessage || 'Connect your verified Shopify account to view purchased tools.')
        : (root.dataset.unavailableMessage || 'Your tools are not available right now. Please try again.');
      if (instance.linkButton) { instance.linkButton.hidden = root.dataset.state !== 'unlinked'; instance.linkButton.disabled = false; }
      return;
    }

    const cards = payload.tools.slice(0, MAX_TOOLS).map((/** @type {any} */ tool) => renderTool(template, tool)).filter(Boolean);
    list.replaceChildren(...cards);
    list.hidden = cards.length === 0;
    empty.hidden = cards.length > 0;
    root.dataset.state = 'ready';
    if (instance.linkButton) instance.linkButton.hidden = true;
    status.textContent = root.dataset.loadedMessage || 'Your tools are up to date.';
  };

  /** @param {Event} event */
  const handleAccountLinkData = (event) => {
    const data = /** @type {CustomEvent<any>} */ (event).detail, instance = instances.get(data?.panelId);
    if (!instance || !instance.linkRequestId || data.requestId !== instance.linkRequestId ||
        instance.sessionStatus !== 'authenticated' || !activeToolsTab()) return;
    const state = window.LittleGWebSession?.getState?.();
    if (state?.status !== 'authenticated' || state.profile?.id !== instance.subject) return;
    instance.linkRequestId = '';
    if (instance.linkButton) instance.linkButton.disabled = false;
    if (data.linked === true) { instance.requestData?.('account-linked'); return; }
    instance.root.dataset.state = 'unlinked';
    if (instance.linkButton) instance.linkButton.hidden = false;
    instance.root.querySelector('[data-my-tools-status]').textContent =
      data.errorCode === 'shopify_session_required'
        ? (instance.root.dataset.shopifySessionRequiredMessage || 'Sign in to Shopify in My LittleG, then reconnect.')
        : (instance.root.dataset.linkUnavailableMessage || 'Account linking is unavailable.');
  };

  // Register before requesting data so a trusted adapter may respond synchronously.
  document.addEventListener('littleg:my-tools-data', handleToolsData);
  document.addEventListener('littleg:my-tools-link-data', handleAccountLinkData);

  const activeToolsTab = () => {
    try { return String(new URL(window.location.href).searchParams.get('tab') || '').toLowerCase() === 'tools'; }

    catch { return false; }
  };

  /** @param {any} root Shopify section root with dynamic data attributes. */
  const mount = (root) => {
    if (instances.has(root.id)) return;
    const controller = new AbortController();
    /** @param {EventTarget} node @param {string} type @param {(event: any) => void} handler */
    const listen = (node, type, handler) => node.addEventListener(type, handler, { signal: controller.signal });
    if (!root.id || instances.has(root.id)) return;
    const list = root.querySelector('[data-my-tools-list]');
    const empty = root.querySelector('[data-my-tools-empty]');
    const status = root.querySelector('[data-my-tools-status]');
    const template = root.querySelector('[data-my-tools-card-template]');
    const guest = root.querySelector('[data-my-tools-guest]');
    const privatePanel = root.querySelector('[data-my-tools-private]');
    const workspace = root.querySelector('[data-my-tools-workflow]');
    const refreshButton = root.querySelector('[data-my-tools-refresh]');
    const linkButton = root.querySelector('[data-my-tools-link-account]');
    if (!list || !empty || !status || !template || !guest || !privatePanel || !refreshButton) return;

    const timeoutMs = Math.min(Math.max(Number(root.dataset.loadingTimeout) || 10000, 3000), 30000);
    /** @type {{root:any,requestId:string,linkRequestId:string,timer:number|null,timeoutMs:number,sessionStatus:string,subject:string,linkButton:any,requestData:((reason:string)=>void)|null}} */
    const instance = { root, requestId: '', linkRequestId: '', timer: null, timeoutMs, sessionStatus: '', subject: '', linkButton, requestData: null };
    instances.set(root.id, instance);


    // CV files belong to the existing LittleGProfile local persistence owner.
    // This presentation never sees or stores account tokens or server entitlements.
    let resumeEpoch = 0;
    const resumeForm = root.querySelector('[data-my-resume-form]');
    const resumeFile = root.querySelector('[data-my-resume-file]');
    const resumeStatus = root.querySelector('[data-my-resume-status]');
    const resumeSaved = root.querySelector('[data-my-resume-saved]');
    const resumeName = root.querySelector('[data-my-resume-name]');
    const resumeDownload = root.querySelector('[data-my-resume-download]');
    const resumeRemove = root.querySelector('[data-my-resume-remove]');
    const resumeSaveButton = root.querySelector('[data-my-resume-save]');
    /** @param {string} en @param {string} zh */
    const resumeMessage = (en, zh) => document.documentElement.lang.startsWith('zh') ? zh : en;
    const resumeSession = () => {
      const state = window.LittleGWebSession?.getState?.();
      return activeToolsTab() && state?.status === 'authenticated' &&
        state.profile?.id === instance.subject && Boolean(instance.subject);
    };
    const showResume = async () => {
      const epoch = ++resumeEpoch;
      if (!resumeSaved || !resumeStatus) return;
      resumeSaved.hidden = true;
      if (!resumeSession()) { resumeStatus.textContent = ''; return; }
      const owner = window.LittleGProfile;
      if (!owner?.resumeInfo) {
        resumeStatus.textContent = resumeMessage('Local CV storage is unavailable.', '本機履歷儲存暫不可用。');
        return;
      }
      try {
        const info = await owner.resumeInfo();
        if (epoch !== resumeEpoch || !resumeSession()) return;
        resumeSaved.hidden = !info;
        resumeName.textContent = info?.name || '';
        resumeStatus.textContent = info
          ? resumeMessage('Saved on this device only. No cloud upload.', '僅保存在本機，並未上傳雲端。')
          : resumeMessage('No CV stored in this browser yet.', '這個瀏覽器尚未保存履歷。');
      } catch (_) {
        // Browser-local storage may be blocked or unavailable.
        if (epoch === resumeEpoch && resumeSession()) {
          resumeStatus.textContent = resumeMessage('Local CV storage is unavailable.', '本機履歷儲存暫不可用。');
        }
      }
    };
    if (resumeForm && resumeFile && resumeSaveButton) listen(resumeForm, 'submit', async (event) => {
      event.preventDefault();
      if (!resumeSession() || resumeSaveButton.disabled) return;
      const owner = window.LittleGProfile;
      const file = resumeFile.files?.[0];
      if (!file || !owner?.resumeSave) return;
      const subject = instance.subject;
      resumeSaveButton.disabled = true;
      resumeStatus.textContent = resumeMessage('Saving CV on this device…', '正在本機保存履歷…');
      try {
        await owner.resumeSave(file);
        if (instance.subject === subject && resumeSession()) {
          resumeFile.value = '';
          await showResume();
        }
      } catch (error) {
        if (instance.subject === subject && resumeSession()) {
          resumeStatus.textContent = error instanceof Error && error.message === 'resume_invalid_file'
            ? resumeMessage('Choose a valid PDF or DOCX under 5 MB.', '請選擇有效的 PDF／DOCX（5 MB 以下）。')
            : resumeMessage('CV could not be saved locally.', '無法在本機保存履歷。');
        }
      } finally { resumeSaveButton.disabled = false; }
    });
    if (resumeDownload) listen(resumeDownload, 'click', async () => {
      if (!resumeSession() || resumeDownload.disabled) return;
      const subject = instance.subject;
      resumeDownload.disabled = true;
      try {
        const record = await window.LittleGProfile?.resumeGet?.();
        if (!record || !resumeSession() || instance.subject !== subject) return;
        const url = URL.createObjectURL(record.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = record.name;
        a.hidden = true;
        document.body.append(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (_) {
        // Blob URL creation or browser-local storage may fail.
        if (resumeSession()) resumeStatus.textContent = resumeMessage('CV download is unavailable.', '暫時無法下載本機履歷。');
      } finally { resumeDownload.disabled = false; }
    });
    if (resumeRemove) listen(resumeRemove, 'click', async () => {
      if (!resumeSession() || resumeRemove.disabled) return;
      const subject = instance.subject;
      resumeRemove.disabled = true;
      try {
        await window.LittleGProfile?.resumeRemove?.();
        if (resumeSession() && instance.subject === subject) await showResume();
      } catch (_) {
        // Fail closed: do not claim the local CV was deleted.
        if (resumeSession()) resumeStatus.textContent = resumeMessage('Could not delete the local CV.', '未能刪除本機履歷。');
      } finally { resumeRemove.disabled = false; }
    });

    /** @param {any} record @param {any} row @param {any} link
     *  @param {any} editor @param {any} message
     *  Reuse the same private-record controls without changing CV persistence.
     */
    const renderLivingCvRow = (record, row, link, editor, message) => {
          link.href = '#'; link.textContent = record.title;
          const status = document.createElement('p'); status.textContent = record.persistence_status;
          const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'littleg-my-tools__button';
          edit.textContent = resumeMessage('Edit', '編輯');
          edit.addEventListener('click', () => {
            const current = window.LittleGProfile.livingCV.snapshot().records.find((/** @type {any} */ entry) => entry.record_id === record.id);
            if (!current || !editor) return;
            editor.reset();
            for (const [key, value] of Object.entries({ ...current.content, record_id: current.record_id, record_type: current.record_type, revision: String(current.revision || 0) })) {
              if (editor.elements.namedItem(key)) editor.elements.namedItem(key).value = value;
            }
            editor.elements.title.focus();
          });
          const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'littleg-my-tools__button';
          remove.textContent = resumeMessage('Delete private record', '刪除私人記錄');
          remove.addEventListener('click', async () => {
            const current = window.LittleGProfile.livingCV.snapshot().records.find((/** @type {any} */ entry) => entry.record_id === record.id);
            try { if (current) await window.LittleGProfile.livingCV.save(current.record_type, {}, current.record_id, true, current.revision); }
            catch (error) { message.textContent = error instanceof Error ? error.message : 'Could not update record.'; }
          });
          const title = document.createElement('strong');
          title.textContent = (['achievement', 'skill'].includes(record.record_type) ? resumeMessage('Self-declared · ', '自行填寫 · ') : '') + record.title;
          row.append(title, status, edit, remove);
          if (record.persistence_status === 'Conflict requires resolution') {
            for (const [choice, label] of [['local', resumeMessage('Use local version (keep recovery copy)', '使用本機版本（保留復原副本）')], ['server', resumeMessage('Use cloud version (keep recovery copy)', '使用雲端版本（保留復原副本）')]]) {
              const resolve = document.createElement('button'); resolve.type = 'button'; resolve.textContent = label; resolve.className = 'littleg-my-tools__button';
              resolve.addEventListener('click', async () => { try { await window.LittleGProfile.livingCV.resolve(record.id, choice); } catch (error) { message.textContent = error instanceof Error ? error.message : 'Could not resolve conflict.'; } });
              row.append(resolve);
            }
          }
    };

    const renderWork = () => {
      const target = root.querySelector('[data-my-work-records]'), message = root.querySelector('[data-my-work-state]');
      if (!target || !message) return;
      target.replaceChildren();
      const sync = window.LittleGProfile?.livingCV?.snapshot();
      const editor = root.querySelector('[data-cv-record-form]');
      if (editor) editor.hidden = !sync?.authorized || sync.owner !== instance.subject;
      const snapshot = window.LittleGFamilyData?.livingCvSnapshot?.();
      const current = window.LittleGWebSession?.getState?.();
      if (current?.status !== 'authenticated' || current.profile?.id !== instance.subject || snapshot?.status !== 'ready') {
        message.dataset.state = 'unavailable';
        message.textContent = document.documentElement.lang.startsWith('zh') ? '已保存作品暫時無法讀取。' : 'Saved work is unavailable.'; return;
      }
      message.dataset.state = snapshot.records.length ? 'ready' : 'empty';
      for (const record of snapshot.records) {
        const row = document.createElement('li'), link = document.createElement('a');
        const destination = new URL(root.querySelector('.littleg-my-tools__portfolio-intro a').href);
        destination.searchParams.set('record_id', record.source_id);
        if (record.source_type === 'living-cv') {
          renderLivingCvRow(record, row, link, editor, message);
          target.append(row);
          continue;
        }
        link.className = 'littleg-my-tools__button littleg-my-tools__button--quiet'; link.href = destination.href; link.textContent = record.title || record.source_type; row.append(link); target.append(row);
      }
      message.textContent = snapshot.records.length ? '' : (document.documentElement.lang.startsWith('zh') ? '尚未有此帳戶的已保存作品。' : 'No saved work for this account.');
    };
    const cvForm = root.querySelector('[data-cv-record-form]');
    if (cvForm) listen(cvForm, 'submit', async event => {
      event.preventDefault();
      const values = new FormData(cvForm);
      /** @param {string} key */
      const value = key => {
        const field = values.get(key);
        return typeof field === 'string' ? field : '';
      };
      /** @type {{title:string,description:string,date?:string}} */
      const content = { title: value('title'), description: value('description') };
      if (value('record_type') !== 'skill') content.date = value('date');
      try {
        await window.LittleGProfile.livingCV.save(value('record_type'), content, value('record_id') || undefined,
          false, value('record_id') ? Number(value('revision')) : undefined);
        cvForm.reset(); cvForm.querySelector('[data-cv-form-status]').textContent = resumeMessage('Saved locally · Sync pending', '已保存於本機 · 待同步');
      } catch (error) { cvForm.querySelector('[data-cv-form-status]').textContent = error instanceof Error ? error.message : 'Could not save record.'; }
    });
    const clearPrivate = () => {
      cvForm?.reset();
      if (cvForm) cvForm.hidden = true;
      ++resumeEpoch;
      if (resumeSaved) resumeSaved.hidden = true;
      if (resumeName) resumeName.textContent = '';
      if (resumeStatus) resumeStatus.textContent = '';
      if (resumeFile) resumeFile.value = '';
      root.querySelector('[data-my-work-records]')?.replaceChildren();
      const workStatus = root.querySelector('[data-my-work-state]'); if (workStatus) { workStatus.textContent = ''; workStatus.dataset.state = 'inactive'; }
      if (instance.timer) window.clearTimeout(instance.timer);
      instance.timer = null;
      instance.requestId = '';
      instance.linkRequestId = '';
      if (linkButton) { linkButton.hidden = true; linkButton.disabled = false; }
      list.replaceChildren();
      list.hidden = true;
      empty.hidden = true;
      if (workspace) {
        workspace.hidden = true;
        workspace.setAttribute('aria-hidden', 'true');
      }
    };

    /** @param {string} reason */
    const requestData = (reason) => {
      if (instance.sessionStatus !== 'authenticated' || !instance.subject || !activeToolsTab()) return;
      clearPrivate();
      instance.requestId = makeRequestId();
      root.dataset.state = 'loading';
      status.textContent = root.dataset.loadingMessage || 'Loading your tools…';
      instance.timer = window.setTimeout(() => {
        if (root.dataset.state !== 'loading') return;
        instance.requestId = ''; // A late response must not be accepted after timeout.
        root.dataset.state = 'unavailable';
        status.textContent = root.dataset.unavailableMessage || 'Your tools are temporarily unavailable.';
      }, instance.timeoutMs);
      root.dispatchEvent(new CustomEvent('littleg:my-tools-request', {
        bubbles: true,
        detail: { panelId: root.id, requestId: instance.requestId, reason }
      }));
    };

    instance.requestData = requestData;

    const syncSession = () => {
      // Portfolio is a Living CV tab, but tools retain their own Core auth gate.
      // Only render private tool states while the existing tools route is active.
      const isPortfolioActive = activeToolsTab();
      root.dataset.portfolioActive = String(isPortfolioActive);
      if (!isPortfolioActive) {
        clearPrivate();
        instance.sessionStatus = '';
        instance.subject = '';
        guest.hidden = true;
        privatePanel.hidden = true;
        root.dataset.state = 'inactive';
        return;
      }
      const session = window.LittleGWebSession;
      const state = session && typeof session.getState === 'function' ? session.getState() : null;
      const nextStatus = state?.status || 'unavailable';
      const previousStatus = instance.sessionStatus;
      const nextSubject = typeof state?.profile?.id === 'string' ? state.profile.id : '';
      const previousSubject = instance.subject;
      instance.sessionStatus = nextStatus;
      instance.subject = nextStatus === 'authenticated' ? nextSubject : '';
      if (nextStatus !== 'authenticated' || !instance.subject) {
        clearPrivate(); // Logout and account changes must never retain private cards.
        privatePanel.hidden = true;
        guest.hidden = nextStatus !== 'guest';
        root.dataset.state = nextStatus;
        let sessionMessage = root.dataset.unavailableMessage || 'Your tools are temporarily unavailable.';
        if (nextStatus === 'guest') {
          sessionMessage = root.dataset.sessionGuestMessage || 'Sign in to LittleG to view your tools.';
        } else if (nextStatus === 'booting') {
          sessionMessage = root.dataset.sessionLoadingMessage || 'Checking your LittleG account…';
        }
        status.textContent = sessionMessage;
        return;
      }
      guest.hidden = true;
      privatePanel.hidden = false;
      if (previousStatus !== 'authenticated' || previousSubject !== instance.subject) {
        requestData('initial');
        void showResume();
      }
      renderWork();
    };

    /** @param {CustomEvent<{toolId?: string, resultId?: string}>} event */
    const openWorkspace = (event) => {
      if (!workspace || !activeToolsTab() || privatePanel.hidden ||
          instance.sessionStatus !== 'authenticated' || !instance.subject) return;
      const session = window.LittleGWebSession;
      const current = session && typeof session.getState === 'function' ? session.getState() : null;
      if (current?.status !== 'authenticated' || current?.profile?.id !== instance.subject) return;
      const toolId = typeof event.detail?.toolId === 'string' ? event.detail.toolId.trim() : '';
      if (!toolId || (event.type === 'littleg:tool-result-open' &&
          typeof event.detail?.resultId !== 'string')) return;
      workspace.hidden = false;
      workspace.setAttribute('aria-hidden', 'false');
    };
    listen(document, 'littleg:tool-selected', openWorkspace);
    listen(document, 'littleg:tool-result-open', openWorkspace);

    listen(refreshButton, 'click', () => requestData('retry'));
    if (linkButton) listen(linkButton, 'click', () => {
      if (root.dataset.state !== 'unlinked' || instance.linkRequestId || !activeToolsTab() ||
          instance.sessionStatus !== 'authenticated' || !instance.subject) return;
      const state = window.LittleGWebSession?.getState?.();
      if (state?.status !== 'authenticated' || state.profile?.id !== instance.subject) return;
      instance.linkRequestId = makeRequestId();
      linkButton.disabled = true;
      root.dataset.state = 'linking';
      status.textContent = root.dataset.linkingMessage || 'Verifying both accounts…';
      root.dispatchEvent(new CustomEvent('littleg:my-tools-link-request', {
        bubbles: true, detail: { panelId: root.id, requestId: instance.linkRequestId }
      }));
    });
    listen(document, 'littleg:web-session-change', syncSession);
    listen(document, 'littleg:dashboard-tab-change', syncSession);
    listen(window, 'pageshow', syncSession);
    listen(window, 'popstate', syncSession);
    for (const name of ['littleg:cv-change', 'littleg:family-record-saved', 'littleg:family-cloud-sync', 'littleg:parent-gate-change']) listen(document, name, syncSession);
    for (const name of ['littleg:city-mission:result-saved', 'littleg:city-mission:result-removed', 'littleg:city-mission:results-cleared']) listen(window, name, syncSession);
    root.__myToolsCleanup = () => { controller.abort(); clearPrivate(); privatePanel.hidden = true; instances.delete(root.id); };
    syncSession();
  };
  /** @param {any} scope Shopify Section or Document root. */
  const boot = scope => { if (scope.matches?.('[data-littleg-my-tools]')) mount(scope); scope.querySelectorAll('[data-littleg-my-tools]').forEach(mount); };
  window.__littleGMyToolsBoot = boot;
  document.addEventListener('shopify:section:load', event => { if (event.target) boot(event.target); });
  document.addEventListener('shopify:section:unload', event => {
    const target = /** @type {MyToolsElement|null} */ (/** @type {unknown} */ (event.target));
    if (!target) return;
    target.__myToolsCleanup?.();
    target.querySelectorAll('[data-littleg-my-tools]').forEach(node => {
      /** @type {MyToolsElement} */ (node).__myToolsCleanup?.();
    });
  });
  boot(document);
})();
